import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import {
  Authenticator,
  useAuthenticator,
  Heading,
  useTheme,
  Flex,
  Divider,
} from '@aws-amplify/ui-react';
import { authService, USER_GROUPS, type User } from '../src/services/authService';
import {
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Container,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Box,
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Description,
  Logout,
  ChevronLeft,
  Assignment,
} from '@mui/icons-material';
import '@aws-amplify/ui-react/styles.css';
import './backend.css';

// Custom theme for the backend - Clean Blue & White
const backendTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#1976d2',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e2e8f0',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f5f9',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: '#f8fafc',
          },
          '&:hover': {
            backgroundColor: '#e0f2fe',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e2e8f0',
          color: '#1e293b',
        },
        head: {
          backgroundColor: '#f1f5f9',
          color: '#1e293b',
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#64748b',
          '&:hover': {
            backgroundColor: '#e0f2fe',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#e0f2fe',
          color: '#1976d2',
          fontWeight: 500,
        },
        outlined: {
          borderColor: '#1976d2',
          color: '#1976d2',
        },
      },
    },
  },
});

const BackendLayout: React.FC = () => {
  const { signOut, authStatus } = useAuthenticator((context) => [
    context.signOut,
    context.authStatus,
  ]);
  const { tokens } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [, setUserLoading] = useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Set body attribute for backend styling
  useEffect(() => {
    document.body.setAttribute('data-backend', 'true');

    // Cleanup function to remove the attribute when component unmounts
    return () => {
      document.body.removeAttribute('data-backend');
    };
  }, []);

  // Load user information
  useEffect(() => {
    const loadUser = async () => {
      if (authStatus === 'authenticated') {
        try {
          setUserLoading(true);
          const user = await authService.getCurrentUser();
          setCurrentUser(user);
          //console.log('BackendLayout - Current user loaded:', user);
          //console.log('BackendLayout - User groups:', user.groups);
        } catch (error) {
          console.error('Failed to load user in BackendLayout:', error);
        } finally {
          setUserLoading(false);
        }
      }
    };

    loadUser();
  }, [authStatus]);

  // Debug log when currentUser changes
  useEffect(() => {
    if (currentUser) {
      console.log('BackendLayout - User group label:', getUserGroupLabel());
    }
  }, [currentUser]);

  // Helper functions for permissions
  const isJudge = useCallback(() => {
    return currentUser && authService.isInGroup(currentUser, USER_GROUPS.JUDGE);
  }, [currentUser]);

  const isSuperAdmin = useCallback(() => {
    return currentUser && authService.isInGroup(currentUser, USER_GROUPS.SUPER_ADMIN);
  }, [currentUser]);

  // Get user's highest group for display
  const getUserGroupLabel = useCallback(() => {
    if (!currentUser || !currentUser.groups || currentUser.groups.length === 0) {
      return '';
    }

    // Get the highest permission group
    const highestGroup = authService.getUserHighestGroup(currentUser);

    // Convert to display-friendly format
    switch (highestGroup) {
      case USER_GROUPS.SUPER_ADMIN:
        return 'Super Admin';
      case USER_GROUPS.ADMIN:
        return 'Admin';
      case USER_GROUPS.JUDGE:
        return 'Judge';
      default:
        return highestGroup;
    }
  }, [currentUser]);

  // Debug log when currentUser changes
  useEffect(() => {
    if (currentUser) {
      console.log('BackendLayout - User group label:', getUserGroupLabel());
    }
  }, [currentUser, getUserGroupLabel]);

  // Redirect judges away from Dashboard
  useEffect(() => {
    if (currentUser && isJudge() && window.location.pathname === '/backend/') {
      // console.log('Redirecting judge away from Dashboard to Applications');
      navigate('/backend/applications');
    }
  }, [currentUser, navigate, isJudge]);

  // Menu items for the sidebar - filtered based on user permissions
  const getMenuItems = () => {
    const allMenuItems = [
      {
        label: 'Dashboard',
        icon: <Dashboard />,
        path: '/backend/',
        showForJudge: false, // Hide Dashboard for judges
        showForAdmin: true,
        showForSuperAdmin: true,
      },
      {
        label: 'Applications',
        icon: <Description />,
        path: '/backend/applications',
        showForJudge: true, // Show Applications for judges
        showForAdmin: true,
        showForSuperAdmin: true,
      },
      {
        label: 'Judge Management',
        icon: <Assignment />,
        path: '/backend/judge-management',
        showForJudge: false, // Hide for judges
        showForAdmin: false, // Hide for regular admins
        showForSuperAdmin: true, // Only show for super admins
      },
    ];

    // Filter menu items based on user role
    if (isJudge()) {
      return allMenuItems.filter(item => item.showForJudge);
    } else if (isSuperAdmin()) {
      return allMenuItems.filter(item => item.showForSuperAdmin);
    } else {
      // Regular admin
      return allMenuItems.filter(item => item.showForAdmin);
    }
  };

  const menuItems = getMenuItems();

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle menu item click
  const handleMenuItemClick = (path: string) => {
    // Navigate to the specified path using React Router
    navigate(path);
    // Always close the sidebar when a menu item is clicked
    setSidebarOpen(false);
  };

  // Show logout confirmation dialog
  const handleSignOutClick = () => {
    setLogoutDialogOpen(true);
  };

  // Confirm logout
  const handleConfirmLogout = async () => {
    setLogoutDialogOpen(false);

    try {
      // console.log('Starting logout process...');

      // Use both Amplify signOut and authService logout
      await Promise.all([
        signOut(),
        authService.logout()
      ]);

      // console.log('Logout successful');

      // Clear any local state
      setCurrentUser(null);

      // The Authenticator will automatically show the login form
      // No need to navigate manually
    } catch (error) {
      console.error('Logout error:', error);

      // Even if signOut fails, try to clear local state
      try {
        // Force clear any local storage or session data
        localStorage.clear();
        sessionStorage.clear();
        setCurrentUser(null);
        // console.log('Cleared local storage and state');
      } catch (clearError) {
        console.error('Error clearing storage:', clearError);
      }
    }
  };

  // Cancel logout
  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  // If not authenticated, show the login form
  if (authStatus !== 'authenticated') {
    return (
      <ThemeProvider theme={backendTheme}>
        <CssBaseline />
        <Container
          maxWidth="sm"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 2,
            }}
          >
            <Authenticator
              hideSignUp={true}
              components={{
                Header() {
                  return (
                    <Heading
                      padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                      level={3}
                    >
                      Smart City Backend
                    </Heading>
                  );
                },
              }}
            />
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }



  // Main layout after authentication
  return (
    <ThemeProvider theme={backendTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Overlay Sidebar */}
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          sx={{
            width: 240,
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box',
              backgroundColor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              top: 0,
              height: '100%',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Flex alignItems="center" justifyContent="space-between">
              <Heading level={4} color={tokens.colors.font.primary}>
                Smart City
              </Heading>
              <IconButton onClick={handleSidebarToggle}>
                <ChevronLeft />
              </IconButton>
            </Flex>
          </Box>
          <Divider />
          <List sx={{ pt: 1 }}>
            {menuItems.map((item) => (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => handleMenuItemClick(item.path)}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 2,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Logout section at bottom of sidebar */}
          <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Account name */}
            {currentUser && (
              <Box sx={{ px: 2.5, py: 1, mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Account
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {currentUser.fullName || currentUser.email}
                </Typography>
                {getUserGroupLabel() && (
                  <Chip
                    label={getUserGroupLabel()}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      mt: 0.5,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      backgroundColor: 'primary.light',
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                )}
              </Box>
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={handleSignOutClick}
                sx={{
                  minHeight: 48,
                  px: 2.5,
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 2,
                    justifyContent: 'center',
                    color: 'error.main',
                  }}
                >
                  <Logout />
                </ListItemIcon>
                <ListItemText
                  primary="Sign Out"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'error.main',
                  }}
                />
              </ListItemButton>
            </ListItem>
          </Box>
        </Drawer>

        {/* Main content area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
          }}
        >
          {/* Top App Bar */}
          <AppBar
            position="static"
            elevation={1}
            sx={{
              backgroundColor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <Toolbar sx={{
              minHeight: 64,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2
            }}>
              {/* Left side - Menu and Title */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleSidebarToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
                  Smart Living Partnership Awards 2025 - Backend System
                </Typography>
              </Box>

              {/* Right side - Account name and Logout button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Account name with group */}
                {currentUser && (
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {currentUser.fullName || currentUser.email}
                    </Typography>
                    {getUserGroupLabel() && (
                      <Chip
                        label={getUserGroupLabel()}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: 'background.paper',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                  </Box>
                )}

                <IconButton
                  onClick={handleSignOutClick}
                  sx={{
                    color: 'error.main',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  title="Sign Out"
                >
                  <Logout />
                </IconButton>
              </Box>


            </Toolbar>
          </AppBar>

          {/* Page content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.default',
              overflow: 'hidden',
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleCancelLogout}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to sign out? You will need to log in again to access the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmLogout} color="error" variant="contained">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug Console - only show in development */}
    </ThemeProvider>
  );
};

export default BackendLayout;
